import { useParams, Link } from 'react-router-dom';
import { MapPin, ArrowRight, ArrowLeft, Building2, Landmark } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getStateBySlug, getCitiesByState, getCountiesByState } from '../../data/locations';

export default function StateIndex() {
  const { state } = useParams<{ state: string }>();
  const stateData = state ? getStateBySlug(state) : undefined;

  if (!stateData) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-44 lg:pb-28">
          <div className="container-custom text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">State Not Found</h1>
            <p className="text-gray-600 mb-8">
              We do not have location data for this state yet.
            </p>
            <Link to="/locations" className="btn-primary">
              View All Locations
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const cities = getCitiesByState(stateData.abbr);
  const counties = getCountiesByState(stateData.abbr);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <Link
              to="/locations"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              All Locations
            </Link>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              {stateData.name}
            </div>

            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Hire faster in{' '}
              <span className="gradient-text">{stateData.name}</span>
            </h1>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {stateData.description} Browse cities and counties below to find hiring solutions near you.
            </p>
          </div>
        </div>
      </section>

      {/* Cities */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-8">
            <Building2 className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              Major Cities ({cities.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {cities.map((loc) => (
              <Link
                key={loc.slug}
                to={`/locations/${state}/${loc.slug}`}
                className="card p-5 group hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors mb-1">
                  {loc.city}
                </h3>
                <p className="text-xs text-gray-500 mb-3 line-clamp-2">{loc.description}</p>
                <div className="flex flex-wrap gap-1.5">
                  {loc.industries.slice(0, 3).map((ind, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-[10px] font-medium"
                    >
                      {ind}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Counties */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="flex items-center gap-3 mb-8">
            <Landmark className="w-6 h-6 text-primary-600" />
            <h2 className="text-2xl font-bold text-gray-900">
              All Counties ({counties.length})
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {counties.map((loc) => (
              <Link
                key={loc.slug}
                to={`/locations/${state}/${loc.slug}`}
                className="flex items-center justify-between px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-all group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 group-hover:text-primary-600 transition-colors flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-700 group-hover:text-primary-700 transition-colors truncate">
                    {loc.city}
                  </span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-primary-600 transition-colors flex-shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to hire faster in {stateData.name}?
            </h2>
            <p className="text-lg text-primary-100 mb-10 max-w-xl mx-auto">
              Join businesses across {stateData.name} using iHireFast to streamline recruiting. Free to start.
            </p>
            <a
              href="https://app.ihirefast.com"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
            >
              Get Started Free
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
